import { cn } from '@pubstack/ui/utils/cn'
import { FunctionComponent } from 'react'
import { WithClassName } from '~/types/utils'
import { CatalogLogo } from './CatalogLogo'
import { Tooltip } from '../Tooltip'

/** Small enough to sit on a table row without setting its height, big enough for a wordmark to read. */
const LOGO_TILE_CLASS = 'flex shrink-0 items-center justify-center overflow-hidden rounded border border-platinum p-0.5'

type BidderIdentityProps = WithClassName & {
  baseCDNUrl: string
  /** Keys the logo, and is the adapter's own code — the two are the same string by construction. */
  code: string
  /**
   * What this row is called on the page it sits on — the company as the catalog names it, or, on the mapping
   * views, the seat's own alias. Falls back to the code where there is neither.
   */
  name: string
  /**
   * The company, where `name` is an alias that does not say who it is. Two seats of the same bidder differ
   * only by their alias, so the alias has to stay on the row; the bidder joins the code in the tooltip.
   */
  bidderName?: string
  /** Sizes the logo tile and the text together, for headers that are not table rows. */
  size?: 'row' | 'title'
}

/**
 * `row` fills the 32px row between its 4px of padding, so the logo is as tall as the line it sits on.
 *
 * The tile grew by the padding it gained plus a little: a wordmark drawn edge to edge against the border
 * read as clipped, and at 24px the smaller marks were hard to tell apart at a glance down a long list.
 */
const TILE_SIZE_CLASS: Record<'row' | 'title', string> = { row: 'size-8', title: 'size-9' }

/**
 * A bidder, as every list should name one: its logo and the company's name.
 *
 * The adapter code is what the data actually holds, and it is what these lists used to print — `appnexus`
 * for Xandr, `rubicon` for Magnite. It is still the thing to quote in a support thread, so it moves into a
 * tooltip rather than off the page: recognisable first, technical on demand.
 */
export const BidderIdentity: FunctionComponent<BidderIdentityProps> = ({ baseCDNUrl, code, name, bidderName, size = 'row', className }) => (
  <Tooltip title={[bidderName && bidderName !== name ? bidderName : undefined, `Adapter: ${code}`].filter(Boolean).join(' — ')}>
    <span className={cn('flex min-w-0 items-center gap-2', className)}>
      <span className={cn(LOGO_TILE_CLASS, TILE_SIZE_CLASS[size])}>
        {/* Overrides the card's sizing: the logo is drawn for a 66px tile, and both the wordmark's bounds
            and the monogram's type would overflow a 24px one. */}
        <CatalogLogo className={'max-h-full max-w-full text-[9px]'} baseCDNUrl={baseCDNUrl} code={code} name={bidderName ?? name} />
      </span>
      <span className={'truncate'}>{name}</span>
    </span>
  </Tooltip>
)
